import React from "react";
import { cn } from "@/utils/cn";

export type AspectRatio = "1:1" | "4:5" | "9:16" | "auto";

interface MediaCardContainerProps {
  aspectRatio?: AspectRatio;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

export const MediaCardContainer: React.FC<MediaCardContainerProps> = ({
  aspectRatio = "4:5",
  selected = false,
  disabled = false,
  className,
  onClick,
  children,
}) => {
  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case "1:1":
        return "aspect-square";
      case "4:5":
        return "aspect-4/5";
      case "9:16":
        return "aspect-9/16";
      case "auto":
        return "";
      default:
        return "aspect-4/5";
    }
  };

  // Borderless card (DD-011) — selection reads as a ring, not a border.
  const getSelectionStyle = () => {
    if (selected) {
      return "ring-2 ring-blue-500";
    }
    return "";
  };

  const getCursorStyle = () => {
    if (disabled) {
      return "cursor-not-allowed";
    }
    if (onClick) {
      return "cursor-pointer";
    }
    return "";
  };

  return (
    <div
      className={cn(
        // Aspect ratio
        getAspectRatioClass(),
        // Base styling
        "relative rounded-lg overflow-hidden transition-all",
        // Interactive states
        getSelectionStyle(),
        getCursorStyle(),
        onClick && !disabled && "hover:shadow-md",
        // Group for hover effects on children
        "group/card",
        // Opacity for disabled state
        disabled && "opacity-60",
        // Custom classes
        className
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};
